// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  bankInstitutionSelectExistingSchema,
  type BankInstitutionSelectExisting,
} from '@client/banks/types/institution';
import { useInstitutionCatalog } from '@client/banks/hooks/use-institution-catalog';
import { useBankUploadData } from '@client/banks/providers/bank-upload-provider';
import { findInstitutionCatalogOptionByListKey } from '@client/banks/utils/institution-catalog';
import { zodResolver } from '@hookform/resolvers/zod';
import { useStepsData } from '@shared/ui-kit/hooks/use-steps';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';

export const useInstitutionSelectExisting = () => {
  const { navigate } = useStepsData();
  const { saveFormData } = useBankUploadData();

  const [selectedListKey, setSelectedListKey] = useState<string | undefined>();
  const catalog = useInstitutionCatalog(selectedListKey);

  const form = useForm<BankInstitutionSelectExisting>({
    resolver: zodResolver(bankInstitutionSelectExistingSchema),
    mode: 'onChange',
    defaultValues: { institutionId: '' },
  });

  const isFormValid = useCallback(
    () => form.formState.isValid,
    [form.formState.isValid],
  );

  const onSelectInstitution = useCallback(
    (listKey: string) => {
      setSelectedListKey(listKey);
      form.setValue('institutionId', listKey, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [form],
  );

  const onContinueFromCatalog = useCallback(async () => {
    const isValid = await form.trigger();

    if (!isValid) {
      return false;
    }

    const data = form.getValues();
    const option = findInstitutionCatalogOptionByListKey(
      catalog.allInstitutionOptions,
      data.institutionId,
    );

    if (!option) {
      return false;
    }

    saveFormData('institution', {
      name: option.name,
      logoUrl: (option.logo_url ?? '').trim(),
    });
    navigate('institution-manual');

    return false;
  }, [catalog.allInstitutionOptions, form, navigate, saveFormData]);

  const goToManualEntry = useCallback(() => {
    navigate('institution-manual');
  }, [navigate]);

  return {
    form,
    catalog,
    isFormValid,
    onContinueFromCatalog,
    onSelectInstitution,
    goToManualEntry,
  };
};
